import { Directive, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { User } from '../../models/user';
import { ErrorService } from '../../services/error.service';
import { OrderService } from '../../services/order.service';
import { SpinnerService } from '../../services/spinner.service';
import { SubscriptionService } from '../../services/subscription.service';
import { UserService } from '../../services/user.service';
import { ValidatorService } from '../../services/validator.service';

export type ShippingLabelContext = 'route' | 'routeStop' | 'routeParcel';

export interface EmailShippingLabelData {
	subject: string;
	body: string;
	emails: string[];
}

/**
 * Shared base class for email shipping label modals.
 *
 * Two modals extend this class today: EmailParcelShippingLabelModalComponent and
 * EmailRouteStopShippingLabelsModalComponent. They share the entire form, label-fetching
 * logic, default-label resolution, and send/error semantics — differing only in:
 *   1. Shipping label context string
 *   2. Which inetUser default label field to read
 *   3. Which service method to invoke on send
 *
 * Subclasses implement the four abstract members and the constructor super() call.
 *
 * EmailRouteShippingLabelsModalComponent intentionally does NOT extend this class.
 * It has structurally different form (extra emailToStops / emailToSpecified checkboxes),
 * custom validation logic (isFormValid based on checkbox state), AfterViewInit timing
 * for chip enable/disable, and dynamically generated default subject/message from
 * routeId + postDate. Forcing it into this base would require enough abstract hooks
 * that the duplication savings would be lost.
 */
@Directive()
export abstract class EmailShippingLabelModalBaseComponent implements OnInit, OnDestroy {
	public form: UntypedFormGroup;
	public shippingLabels: any[] = [];
	public subscriptions: any[] = [];
	public user: User = new User();

	constructor(
		protected _formBuilder: UntypedFormBuilder,
		@Inject(MAT_DIALOG_DATA) public data: any,
		protected _validatorService: ValidatorService,
		protected _spinnerService: SpinnerService,
		protected _errorService: ErrorService,
		protected _orderService: OrderService,
		protected _userService: UserService,
		protected _subscriptionService: SubscriptionService,
		protected _dialogRef: MatDialogRef<any>
	) {}

	protected abstract get context(): ShippingLabelContext;
	protected abstract get titleKey(): string;
	protected abstract getDefaultLabelId(user: User): number | null | undefined;
	protected abstract performSend(emailData: EmailShippingLabelData, shippingLabelId: number): Observable<any>;
	protected abstract get failureLogLabel(): string;

	ngOnInit() {
		this.form = this._formBuilder.group({
			email: ['', [Validators.email]],
			emails: [this.data?.defaultEmails || [], [this._validatorService.minArrayLength(1)]],
			selectedShippingLabel: [null, [Validators.required]],
			subject: [this.data?.subject || null, [Validators.required]],
			message: [this.data?.message || null, [Validators.required]]
		});

		this._userService.user$.pipe(take(1)).subscribe(user => {
			this.user = user;
			this.getAvailableShippingLabels();
		});

		this.subscriptions.push(this.form.get('emails').valueChanges.subscribe(value => {
			if (value?.length === 10) {
				this.form.get('email').disable();
			} else if (this.form.get('email').disabled && value?.length < 10) {
				this.form.get('email').enable();
			}
		}));
	}

	ngOnDestroy() {
		this._subscriptionService.unSubscribe(this.subscriptions);
	}

	getAvailableShippingLabels() {
		this._orderService.getAvailableShippingLabels(this.context).subscribe(response => {
			if (response && response.length > 0) {
				this.shippingLabels = response;
				const defaultId = this.getDefaultLabelId(this.user);
				const defaultLabel = defaultId
					? response.find(l => l.procedureId === defaultId)
					: null;
				this.form.controls.selectedShippingLabel.setValue(
					defaultLabel ? defaultLabel.procedureId : response[0].procedureId
				);
				this.form.updateValueAndValidity();
			}
		}, error => {
			this._errorService.error(error, 'getAvailableShippingLabels Failed', 'modal');
		});
	}

	send() {
		if (this.form.invalid) {
			if (this.form.get('emails').errors) {
				this.form.get('email').setErrors({ minLength: true });
				this.form.get('email').markAsTouched();
			}
			return;
		}

		this._spinnerService.startSpinner();

		const emailData: EmailShippingLabelData = {
			subject: this.form.value.subject,
			body: this.form.value.message,
			emails: this.form.value.emails
		};

		this.performSend(emailData, this.form.value.selectedShippingLabel).subscribe(() => {
			this._spinnerService.stopSpinner();
			this.close(true);
		}, error => {
			this._spinnerService.stopSpinner();
			this._errorService.error(error, this.failureLogLabel, 'modal');
		});
	}

	close(result: boolean = false) {
		this._dialogRef.close(result);
	}
}
